import {
  useChatHistoryQuery,
  useChatWithAgentMutation,
  useGetChatsQuery,
} from "~/shared/api/chat/chat.api";
import { useRef, useEffect, useState } from "react";
import { ChatActions } from "./chat-actions";
import { MessageBubble } from "./message-bubble";
import { Namespace, useT } from "@racoon/i18n";
import { IcBackTo } from "@racoon/icons";
import { MessageWithCharts } from "./chat-chart";
import { format, isToday, isYesterday, isSameDay } from "date-fns";
import { ru } from "date-fns/locale";

const formatDateLabel = (date: Date): string => {
  if (isToday(date)) return "Сегодня";
  if (isYesterday(date)) return "Вчера";
  return format(date, "d MMMM yyyy", { locale: ru });
};

const DateSeparator = ({ date }: { date: Date }) => (
  <div className="flex items-center gap-2 my-2">
    <div className="flex-1 h-px bg-gray-400" />
    <span className="text-xs text-gray-500 font-medium px-2">{formatDateLabel(date)}</span>
    <div className="flex-1 h-px bg-gray-400" />
  </div>
);

export const ChatContent = ({
  chatId,
  chatTitle,
  setActiveChatId,
  setIsCreatingNewChat,
  onMessageSelect,
}: {
  chatId: string | null;
  chatTitle: string | null;
  setActiveChatId: (chatId: string | null) => void;
  setIsCreatingNewChat: (isCreating: boolean) => void;
  onMessageSelect: (message: MessageWithCharts) => void;
  selectedMessage: MessageWithCharts | null;
}) => {
  const t = useT();
  const { data: messages, refetch } = useChatHistoryQuery(chatId as string, {
    skip: !chatId,
  });
  const [chatWithAgent, { isLoading }] = useChatWithAgentMutation();
  const { refetch: refetchChats } = useGetChatsQuery();
  const [input, setInput] = useState("");
  const [pendingUserMsg, setPendingUserMsg] = useState<string | null>(null);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [stickyLabel, setStickyLabel] = useState<string | null>(null);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const containerTop = container.getBoundingClientRect().top;
    const groups = container.querySelectorAll<HTMLElement>("[data-date]");
    let current: string | null = null;
    groups.forEach((el) => {
      if (el.getBoundingClientRect().top <= containerTop + 40) {
        current = el.getAttribute("data-date");
      }
    });
    setStickyLabel(current);
  };

  // Synchronous refs for new-message detection (no useEffect delay = no flash)
  const prevChatIdRef = useRef<string | null | undefined>(undefined);
  const prevHistoryCountRef = useRef<number | null>(null);
  const animatingIdxRef = useRef<number | null>(null);
  // Set to true when we create a new chat so we animate the first bot response
  const shouldAnimateInitialRef = useRef(false);

  // Reset on chat change
  if (prevChatIdRef.current !== chatId) {
    prevChatIdRef.current = chatId;
    prevHistoryCountRef.current = null;
    animatingIdxRef.current = null;
  }

  const history = messages?.history || [];

  // Only init once we have real data — prevents animating on initial chat open
  if (prevHistoryCountRef.current === null) {
    if (history.length > 0) {
      if (shouldAnimateInitialRef.current && history[history.length - 1]?.role !== "user") {
        animatingIdxRef.current = history.length - 1;
        shouldAnimateInitialRef.current = false;
      }
      prevHistoryCountRef.current = history.length;
    }
  } else if (history.length > prevHistoryCountRef.current) {
    if (history[history.length - 1]?.role !== "user") {
      animatingIdxRef.current = history.length - 1;
    }
    prevHistoryCountRef.current = history.length;
  }

  const animatingIdx = animatingIdxRef.current;

  const allMessages: MessageWithCharts[] = [
    ...(history as MessageWithCharts[]),
    ...(pendingUserMsg ? [{ content: pendingUserMsg, role: "user" }] : []),
    ...(errorMsg ? [{ content: errorMsg, role: "assistant" }] : []),
  ];

  const isAtBottom = () => {
    const el = scrollContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 100;
  };

  // Scroll to bottom when new message arrives
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [allMessages.length, showSkeleton]);

  // Init sticky label on chat change or first load
  useEffect(() => {
    setTimeout(handleScroll, 0);
  }, [chatId, allMessages.length]);

  const handleSend = async (value?: string) => {
    const messageToSend = value ?? input;
    if (!messageToSend || isLoading) return;
    setErrorMsg(null);
    setPendingUserMsg(messageToSend);
    setShowSkeleton(true);
    const payload = chatId
      ? { chat_id: chatId, user_question: messageToSend }
      : { user_question: messageToSend };
    setInput("");
    try {
      const response = await chatWithAgent(payload).unwrap();
      if (!chatId && response) {
        if (!response.chat_id) return;
        shouldAnimateInitialRef.current = true;
        setActiveChatId(response.chat_id);
        refetchChats();
      } else {
        refetch();
      }
    } catch (err: any) {
      const msg =
        err?.data?.detail ||
        err?.data?.message ||
        err?.message ||
        t(Namespace.Chat, "errorSendingMessage");
      setErrorMsg(msg);
    } finally {
      setPendingUserMsg(null);
      setShowSkeleton(false);
    }
  };

  return (
    <div className="relative h-full flex flex-col gap-4">
      <div className="flex justify-center items-center relative min-h-[24px]">
        <div
          className="absolute -left-2 p-2 cursor-pointer w-fit"
          onClick={() => {
            setActiveChatId(null);
            setIsCreatingNewChat(false);
          }}
        >
          <IcBackTo height={16} width={16} />
        </div>
        <div
          className="text-base font-medium text-center w-fit max-w-[calc(100%-56px)] truncate"
          title={chatTitle || ""}
        >
          {chatTitle ? chatTitle : t(Namespace.Chat, "newChat")}
        </div>
      </div>
      <div className="flex flex-col gap-4 flex-1 min-h-0 lg:mx-0 -mx-[16px] border-t">
        <div className="flex-1 min-h-0 relative">
          {stickyLabel && (
            <div className="absolute top-2 left-0 right-0 flex justify-center z-10 pointer-events-none">
              <span className="bg-white/80 backdrop-blur-sm text-gray-500 text-xs font-medium px-3 py-1 rounded-full shadow-sm">
                {stickyLabel}
              </span>
            </div>
          )}
          <div
            ref={scrollContainerRef}
            className="h-full overflow-y-auto flex flex-col p-2 bg-brand-blue-light lg:rounded-lg"
            onScroll={handleScroll}
          >
            {!chatId && !pendingUserMsg ? (
              <div className="flex-1 flex items-center justify-center text-gray-500 text-base font-medium">
                {t(Namespace.Chat, "writeMessageToStartChat")}
              </div>
            ) : (
              <>
                {allMessages.map((msg, idx) => {
                  const currentDate = msg.timestamp ? new Date(msg.timestamp) : null;
                  const prevDate =
                    idx > 0 && allMessages[idx - 1].timestamp
                      ? new Date(allMessages[idx - 1].timestamp)
                      : null;
                  const showSeparator =
                    currentDate && (!prevDate || !isSameDay(currentDate, prevDate));

                  return (
                    <div
                      key={idx}
                      className="flex flex-col"
                      data-date={currentDate ? formatDateLabel(currentDate) : ""}
                    >
                      {showSeparator && <DateSeparator date={currentDate} />}
                      <MessageBubble
                        timestamp={msg.timestamp}
                        content={msg.content}
                        isUser={msg.role === "user"}
                        isSkeleton={false}
                        onClick={() => onMessageSelect(msg)}
                        animate={idx === animatingIdx}
                        hasCharts={!!msg.charts?.length}
                        onTypingTick={() => {
                          if (isAtBottom()) messagesEndRef.current?.scrollIntoView();
                        }}
                      />
                    </div>
                  );
                })}
                {showSkeleton && <MessageBubble content="" isUser={false} isSkeleton={true} />}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>
        </div>
        <ChatActions
          input={input}
          onInputChange={setInput}
          onSend={handleSend}
          loading={isLoading}
        />
      </div>
    </div>
  );
};
